import { Component, EventEmitter, Inject, inject, OnInit, Optional, Output } from "@angular/core";
import { KeyValuePipe } from "@angular/common";
import { ReactiveFormsModule } from "@angular/forms";
import { ButtonComponent, ModalComponent } from "@evc/web-components";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { DatePicker } from "primeng/datepicker";
import { DialogModule } from "primeng/dialog";
import { InputTextModule } from "primeng/inputtext";
import { MultiSelectModule } from "primeng/multiselect";
import { SelectModule } from "primeng/select";
import { LiveAssetNavigationService, RoleService } from "@rvi/asset-modules";
import { InspectionDetailNew } from "../_objects/inspection";
import { CommonSnackBarService } from "../_services/common-snack-bar.service";
import { InspectionService } from "../_services/http-api/inspection.service";
import { SpinnerService } from "../_services/spinner.service";
import { EVC_MODAL_DATA, EVC_MODAL_REF } from "../_shared/services/modal/evc-modal.service";
import { InspectionFormService } from "./inspection-form.service";
import { INSPECTION_STATUS_CODE } from "../app.constants";
import { NetworkStatusService } from "../_services/network-status.service";

@Component({
  selector: "app-create-inspection-modal",
  imports: [
    KeyValuePipe,
    DialogModule,
    SelectModule,
    MultiSelectModule,
    ButtonComponent,
    InputTextModule,
    ModalComponent,
    ReactiveFormsModule,
    DatePicker,
    TranslateModule
  ],
  templateUrl: "./create-inspection-modal.component.html",
  styleUrl: "./create-inspection-modal.component.scss",
  providers: [InspectionFormService]
})
export class CreateInspectionModalComponent implements OnInit {
  @Output() public inspectionCreated = new EventEmitter<string>();
  private modalRef = inject(EVC_MODAL_REF);

  public canCreateLiveAsset: boolean;

  constructor(
    private inspectionService: InspectionService,
    private translateService: TranslateService,
    private spinnerSerivce: SpinnerService,
    private snackBarService: CommonSnackBarService,
    public formService: InspectionFormService,
    private networkStatusService: NetworkStatusService,
    private liveAssetNavigationService: LiveAssetNavigationService,
    private roleService: RoleService,
    @Optional() @Inject(EVC_MODAL_DATA) private modalData: { liveAssetId?: string } | null
  ) {
    this.canCreateLiveAsset = this.roleService.canI("CreateLiveAsset");
  }

  ngOnInit(): void {
    if (this.modalData?.liveAssetId) {
      this.formService.preselectAsset(this.modalData.liveAssetId);
    }
  }

  public get form() {
    return this.formService.form;
  }

  public readonly titleErrorTranslationKeys: Record<string, string> = {
    required: "inspectionForm.errors.titleRequired",
    maxlength: "inspectionForm.errors.titleMaxLength",
    windowsForbiddenChars: "inspectionForm.errors.titleWindowsForbiddenChars"
  };

  public get isOnPremise(): boolean {
    return this.networkStatusService.isOnPremise;
  }

  private createInspection() {
    this.spinnerSerivce.show();
    this.inspectionService.addInspection(this.createNewInspection()).subscribe({
      next: (createdInspection) => {
        this.inspectionCreated.emit(createdInspection.id);
        this.spinnerSerivce.hide();
        this.onClose();
      },
      error: (error) => {
        this.snackBarService.open(this.translateService.instant("inspectionForm.errors.createUnknownError"));
        this.spinnerSerivce.hide();
        console.error("Error creating inspection:", error);
      }
    });
  }

  public save(): void {
    this.form.controls.title.setValue(this.form.controls.title.value?.trim() ?? "");
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.createInspection();
  }

  private createNewInspection() {
    const formValue = this.form.getRawValue();
    const createInspection: InspectionDetailNew = new InspectionDetailNew();
    createInspection.title = formValue.title;
    createInspection.inspectionDate = formValue.inspectionDate.toISOString();
    createInspection.inspectors = formValue.inspectors.map((id) => ({ id, isActive: true, isSubscription: true }));
    createInspection.subjectId = formValue.liveAsset;
    createInspection.schemaVersion = "0";
    createInspection.status = INSPECTION_STATUS_CODE.NOTINSPECTED;
    return createInspection;
  }

  onClose() {
    this.modalRef.close();
    this.form.reset();
  }

  goToCreateLiveAsset() {
    this.liveAssetNavigationService.goToCreateLiveAsset();
    this.modalRef.close();
  }
}
